import { ConfirmationNumberOutlined } from '@mui/icons-material';
import {
    Alert,
    AlertTitle,
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useField, useFormikContext } from 'formik';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import FormField from 'components/common/formikForm/formField';
import FormWrapper from 'components/common/formikForm/formWrapper';
import SubmitButton from 'components/common/formikForm/SubmitButton';

import { AppDocument } from 'api';
import { ACTIONS } from 'constant';
import { TICKET_URGENCY_CONFIG, TicketStatus } from 'data/constants/constants';
import { useAgents } from 'hooks/useAgents';
import useTranslate from 'hooks/useTranslate';
import { RootState } from 'redux/store';
import { OptionInterface, TicketFormInterface, TicketInterface } from 'types';
import { getTicketStatusLabelKey, getTicketUrgencyLabelKey } from 'utils/ticket';
import { ticketInitialValues, ticketValidationSchema } from 'utils/validations/ticket';

interface TicketFormProps {
    isEdit: boolean;
    disabled?: boolean;
    documents: AppDocument[];
    values?: TicketInterface;
    existingTickets?: TicketInterface[];
    onClose: () => void;
    onSubmit: (payload: TicketInterface) => void;
    onEditExistingTicket?: (ticket: TicketInterface) => void;
    handleAction: (action: ACTIONS, ticket: TicketFormInterface | TicketInterface) => void;
}

interface TicketFieldsProps {
    isEdit?: boolean;
    documentOptions: OptionInterface[];
    ticketCounts: Record<number, number>;
    disabled?: boolean;
    existingTickets?: TicketInterface[];
    onEditExistingTicket?: (ticket: TicketInterface) => void;
    onClose: () => void;
    handleAction: (action: ACTIONS, ticket: TicketFormInterface | TicketInterface) => void;
}

const AssignToField = ({
    agentOptions,
    disabled,
}: {
    agentOptions: OptionInterface[];
    disabled?: boolean;
}) => {
    const { entity: activeApp } = useSelector((state: RootState) => state.application);

    const userOptions = useMemo(() => {
        if (!activeApp) return [];

        const { backoffice_id, consultant_id, sales_id } = activeApp;
        const uniqueIds = new Set([backoffice_id, consultant_id, sales_id]);

        return [...agentOptions].sort((a, b) => {
            const aPriority = uniqueIds.has(+a.value) ? 0 : 1;
            const bPriority = uniqueIds.has(+b.value) ? 0 : 1;
            return aPriority - bPriority;
        });
    }, [activeApp, agentOptions]);

    return (
        <FormField
            type="autosuggest"
            name="assigned_to_id"
            label="assignTo"
            disabled={disabled}
            options={userOptions}
        />
    );
};

const DocumentField = ({
    documentOptions,
    ticketCounts,
    disabled,
}: {
    documentOptions: OptionInterface[];
    ticketCounts: Record<number, number>;
    disabled?: boolean;
}) => {
    const { translate } = useTranslate();
    const [field, meta, helpers] = useField('document_id');

    return (
        <Autocomplete
            fullWidth
            size="small"
            disabled={disabled}
            options={documentOptions}
            value={
                documentOptions.find((opt) => String(opt.value) === String(field.value ?? '')) ||
                null
            }
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => String(option.value) === String(value.value)}
            onChange={(_, value) => {
                helpers.setValue(value ? value.value : '');
            }}
            onBlur={() => helpers.setTouched(true)}
            renderOption={(props, option) => {
                const count = ticketCounts[option.value as number] || 0;
                return (
                    <li {...props} key={String(option.value)}>
                        <Stack
                            direction="row"
                            sx={{
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                width: '100%',
                            }}
                        >
                            <Typography variant="body2" noWrap>
                                {option.label}
                            </Typography>
                            {count > 0 && (
                                <Stack
                                    direction="row"
                                    spacing={0.5}
                                    sx={{ alignItems: 'center', ml: 1 }}
                                >
                                    <ConfirmationNumberOutlined
                                        sx={{ fontSize: 14, color: 'primary.main' }}
                                    />
                                    <Typography
                                        variant="caption"
                                        sx={{ fontWeight: 700, color: 'primary.main' }}
                                    >
                                        {count}
                                    </Typography>
                                </Stack>
                            )}
                        </Stack>
                    </li>
                );
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    size="small"
                    label={`${translate('document')} *`}
                    error={Boolean(meta.touched && meta.error)}
                    helperText={meta.touched && meta.error ? meta.error : ' '}
                />
            )}
        />
    );
};

const TicketFields = ({
    isEdit,
    documentOptions,
    ticketCounts,
    disabled,
    existingTickets,
    onEditExistingTicket,
    onClose,
    handleAction,
}: TicketFieldsProps) => {
    const { translate } = useTranslate();
    const agentOptions = useAgents();
    const { values } = useFormikContext<TicketFormInterface>();
    const selectedDocId = values.document_id ? Number(values.document_id) : null;

    const statusOptions = useMemo(
        () =>
            Object.values(TicketStatus).map((status) => ({
                value: status,
                label: translate(getTicketStatusLabelKey(status)),
            })),
        [translate]
    );

    const urgencyOptions = useMemo(
        () =>
            Object.keys(TICKET_URGENCY_CONFIG).map((key) => ({
                label: translate(getTicketUrgencyLabelKey(key)),
                value: key,
            })),
        [translate]
    );

    const matchingTickets = useMemo(() => {
        if (!selectedDocId || !existingTickets) return [];
        return existingTickets
            .filter((ticket) => Number(ticket.document_id) === selectedDocId)
            .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());
    }, [existingTickets, selectedDocId]);

    const hasConflict = matchingTickets.length > 0 && !isEdit;
    const latestTicket = matchingTickets[0] ?? null;
    const docName = documentOptions.find((doc) => doc.value === selectedDocId)?.label;

    return (
        <>
            <DialogContent
                dividers
                sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', pt: 2 }}
            >
                <Stack spacing={0.5}>
                    {isEdit ? (
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1}
                            sx={{
                                alignItems: { xs: 'stretch', sm: 'center' },
                                justifyContent: 'space-between',
                                px: 1.5,
                                py: 1.25,
                                mb: 1,
                                borderRadius: 1,
                                bgcolor: 'action.hover',
                            }}
                        >
                            <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontWeight: 700, textTransform: 'uppercase' }}
                                >
                                    {translate('tagToDocument')}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {docName}
                                </Typography>
                            </Stack>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleAction(ACTIONS.PREVIEW_FILE, values)}
                                sx={{ textTransform: 'none', flexShrink: 0 }}
                            >
                                {translate('viewDocument')}
                            </Button>
                        </Stack>
                    ) : (
                        <DocumentField
                            documentOptions={documentOptions}
                            ticketCounts={ticketCounts}
                            disabled={disabled}
                        />
                    )}

                    {hasConflict ? (
                        <Alert
                            severity="warning"
                            action={
                                latestTicket ? (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => onEditExistingTicket?.(latestTicket)}
                                        sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                                    >
                                        {translate('editTicket')}
                                    </Button>
                                ) : null
                            }
                        >
                            <AlertTitle sx={{ fontWeight: 700, fontSize: 13 }}>
                                {translate('ticketAlreadyExists').replace(
                                    '{{id}}',
                                    String(latestTicket?.id ?? '')
                                )}
                            </AlertTitle>
                            <Typography variant="caption" color="text.secondary">
                                {translate('ticketAlreadyExistsDescription')}
                            </Typography>
                        </Alert>
                    ) : (
                        <>
                            <FormField
                                required
                                size="small"
                                name="text"
                                label="title"
                                disabled={disabled}
                            />
                            <FormField
                                size="small"
                                name="description"
                                label="description"
                                multiline
                                minRows={3}
                                disabled={disabled}
                            />
                            {isEdit ? (
                                <FormField
                                    required
                                    type="radio"
                                    size="small"
                                    name="status"
                                    label="status"
                                    options={statusOptions}
                                    disabled={disabled}
                                />
                            ) : null}
                            <FormField
                                type="radio"
                                size="small"
                                name="urgency"
                                label="urgency"
                                options={urgencyOptions}
                                disabled={disabled}
                            />
                            <AssignToField agentOptions={agentOptions} disabled={disabled} />
                        </>
                    )}
                </Stack>
            </DialogContent>

            {hasConflict ? (
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button variant="outlined" onClick={onClose} sx={{ textTransform: 'none' }}>
                        {translate('close')}
                    </Button>
                </DialogActions>
            ) : (
                <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        disabled={disabled}
                        sx={{ textTransform: 'none' }}
                    >
                        {translate('cancel')}
                    </Button>
                    <SubmitButton loading={disabled} sx={{ textTransform: 'none' }}>
                        {isEdit ? 'saveChanges' : 'createTicket'}
                    </SubmitButton>
                </DialogActions>
            )}
        </>
    );
};

const TicketForm = ({
    values,
    documents,
    existingTickets,
    isEdit,
    disabled,
    onClose,
    onEditExistingTicket,
    onSubmit,
    handleAction,
}: TicketFormProps) => {
    const { translate } = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const ticketCounts = useMemo(() => {
        const counts: Record<number, number> = {};
        existingTickets?.forEach((ticket) => {
            const docId = Number(ticket.document_id);
            counts[docId] = (counts[docId] || 0) + 1;
        });
        return counts;
    }, [existingTickets]);

    const documentOptions = useMemo(
        () =>
            (documents ?? []).map((doc) => ({
                label: doc.document_name,
                value: doc.id,
            })),
        [documents]
    );

    const title = translate(isEdit ? 'editTicket' : 'createTicket');

    return (
        <Dialog
            open
            fullWidth
            maxWidth="sm"
            fullScreen={isMobile}
            scroll="paper"
            slotProps={{
                paper: {
                    sx: {
                        '& > form': {
                            display: 'flex',
                            flexDirection: 'column',
                            flex: '1 1 auto',
                            minHeight: 0,
                            overflow: 'hidden',
                        },
                    },
                },
            }}
        >
            <FormWrapper<TicketInterface>
                enableReinitialize
                initialValues={values ?? (ticketInitialValues as TicketInterface)}
                validationSchema={ticketValidationSchema}
                onSubmit={onSubmit}
            >
                <DialogTitle>
                    {title}
                    {!isEdit ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {translate('createTicketModalSubtitle')}
                        </Typography>
                    ) : null}
                </DialogTitle>
                <TicketFields
                    isEdit={isEdit}
                    documentOptions={documentOptions}
                    ticketCounts={ticketCounts}
                    disabled={disabled}
                    existingTickets={existingTickets}
                    onEditExistingTicket={onEditExistingTicket}
                    onClose={onClose}
                    handleAction={handleAction}
                />
            </FormWrapper>
        </Dialog>
    );
};

export default TicketForm;
